import { useTranslation } from 'react-i18next';
import { VdsButton, VdsNotification } from '@volt/volt-extended-components-react';
import { useCallback, useEffect, useMemo } from 'react';
import { LoginRequest, useLoginMutation } from '@casops/store/apis/authApi';
import { navToHomePage } from '@casops/navigation';
import Link from '@casops/routes/root/components/link/Link';
import { FORGOT_PASSWORD_PAGE, PASSWORD_RESET_SUCCESS } from '@casops/shared/constants';
import RootPageContainer from '../components/root-page-container/RootPageContainer';
import RootPageCard from '../components/root-page-card/RootPageCard';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import CasFormInput from '@cas/components/inputs/cas-form-input/CasFormInput';
import { useState } from 'react';
import { useCanSubmit } from '@casops/hooks/useCanSubmit';

type FormModel = LoginRequest;

const defaultValues: FormModel = {
  username: '',
  password: '',
};

export function LandingPage() {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | undefined>(undefined);
  const [login, { isLoading }] = useLoginMutation();

  useEffect(() => {
    const passwordResetSuccess: boolean =
      sessionStorage.getItem(PASSWORD_RESET_SUCCESS) === 'true' || false;
    passwordResetSuccess && setSuccessMessage(t('PasswordResetSuccess'));
    sessionStorage.removeItem(PASSWORD_RESET_SUCCESS);
  }, [t]);

  const schema = useMemo(() => {
    return z.object({
      username: z.string().min(1, { message: t('EnterAUsername') }),
      password: z.string().min(1, { message: t('EnterAPassword') }),
    });
  }, [t]);

  const {
    handleSubmit,
    control,
    formState: { isSubmitting },
  } = useForm<FormModel>({
    defaultValues,
    resolver: zodResolver(schema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const { canSubmit, setDisableSubmit } = useCanSubmit(isSubmitting, isLoading);

  const onSubmit = useCallback(
    async (data: FormModel) => {
      setErrorMessage(false);
      setSuccessMessage(undefined);

      await login(data)
        .unwrap()
        .then(() => {
          setDisableSubmit(true);
          navToHomePage();
        })
        .catch(error => {
          setErrorMessage(true);
        });
    },
    [login, setDisableSubmit],
  );

  return (
    <RootPageContainer>
      <RootPageCard
        subHeading={t('DigitalMineCasOps')}
        fields={
          <>
            {successMessage && (
              <VdsNotification variant="inline" status="healthy" showCloseButton={false}>
                <span slot="body">{successMessage}</span>
              </VdsNotification>
            )}
            {errorMessage && (
              <VdsNotification variant="inline" status="danger" showCloseButton={false}>
                <div slot="header">{t('SignInFailed')}</div>
                <span slot="body">{t('SignInFailedDetailed')}</span>
              </VdsNotification>
            )}
            <CasFormInput
              control={control}
              name="username"
              placeholder={t('Username')}
              type="text"
              autocomplete="username"
              disabled={!canSubmit}
              onEnterPressed={() => handleSubmit(onSubmit)()}
              size="large"
            />
            <CasFormInput
              control={control}
              name="password"
              placeholder={t('Password')}
              type="password"
              autocomplete="current-password"
              disabled={!canSubmit}
              onEnterPressed={() => handleSubmit(onSubmit)()}
              size="large"
            />
          </>
        }
      >
        <VdsButton
          id="sign-in-button"
          variant="primary"
          size="large"
          danger
          disabled={!canSubmit}
          onClick={handleSubmit(onSubmit)}
          isFullWidth
        >
          {t('SignIn')}
        </VdsButton>
        <Link to={FORGOT_PASSWORD_PAGE} title={t('ForgotPassword')}></Link>
      </RootPageCard>
    </RootPageContainer>
  );
}

export default LandingPage;
